import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { OrganizationFormValues } from '@/features/organizations';
import type { OrganizationMember, OrganizationTitle } from '@/features/organizations/utils/membership-sync';
import type { EntitySummary, Organization, Relationship } from '@/lib/types';

import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { MultiSelectComboBox, type ComboboxOption } from '@/components/multi-select-combobox';
import { RelationshipEditorDialog } from '@/components/relationship-editor-dialog';
import {
  RelationshipGraph,
  type GraphPosition,
  type RelationshipGraphConnectionEndpoint,
  type RelationshipGraphEntity,
  type RelationshipGraphShapeTheme,
} from '@/components/relationship-graph';
import { useCharacters } from '@/features/characters';
import { ORGANIZATION_HIERARCHY_FEATURE_KEY } from '@/features/organizations/utils/organization-hierarchy-feature';
import { useEntities } from '@/hooks/use-entities';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';


const CHARACTER_MEMBER_PREFIX = 'character:';
const ORGANIZATION_MEMBER_PREFIX = 'organization:';
const GRAPH_CANVAS_HEIGHT = 560;

const toCharacterMemberValue = (characterId: string) => `${CHARACTER_MEMBER_PREFIX}${characterId}`;
const toOrganizationMemberValue = (organizationId: string) => `${ORGANIZATION_MEMBER_PREFIX}${organizationId}`;
const fromCharacterMemberValue = (value: string) => value.slice(CHARACTER_MEMBER_PREFIX.length);
const fromOrganizationMemberValue = (value: string) => value.slice(ORGANIZATION_MEMBER_PREFIX.length);

type ChildOrganizationTitles = Record<string, string>;

type MembershipSectionProps = {
  organizationId?: string;
  enableRelationshipEditing?: boolean;
  disableAutoSave?: boolean;
  isFormSubmitting?: boolean;
  fitViewDependency?: any;
};

function cleanTitle(value: string) {
  return value.trim();
}

function addTitleSuggestion(suggestions: Set<string>, value: unknown) {
  if (typeof value !== 'string') return;
  const title = cleanTitle(value);
  if (title) suggestions.add(title);
}

function getLegacyTitleName(titles: OrganizationTitle[], titleId?: string) {
  if (!titleId) return '';
  return titles.find((title) => title.id === titleId)?.name || '';
}

function getFallbackEntitySummary(id: string, name: string, type: string): EntitySummary {
  return {
    id,
    name,
    type,
    shortDescription: '',
    primaryImageId: null,
    updatedAt: '',
  };
}

function OrganizationRelationshipsGraph({
  entities,
  relationships,
  novelId,
  novelTheme,
  shapeThemeOverride,
  isRelationshipEditingEnabled = false,
  isRelationshipSaving = false,
  onCreateRelationship,
  onRelationshipClick,
  onLayoutPositionsChange,
  onSaveLayoutPositions,
  onShapeThemeChange,
  suppressLayoutNavigationBlocker = false,
  fitViewDependency,
}: {
  entities: RelationshipGraphEntity[];
  relationships: Relationship[];
  novelId?: string | null;
  novelTheme?: string | null;
  shapeThemeOverride?: RelationshipGraphShapeTheme | null;
  isRelationshipEditingEnabled?: boolean;
  isRelationshipSaving?: boolean;
  onCreateRelationship?: (
    source: RelationshipGraphConnectionEndpoint,
    target: RelationshipGraphConnectionEndpoint,
  ) => void;
  onRelationshipClick?: (relationship: Relationship) => void;
  onLayoutPositionsChange?: (positions: Record<string, GraphPosition>) => void;
  onSaveLayoutPositions?: (positions: Record<string, GraphPosition>) => Promise<void>;
  onShapeThemeChange?: (shapeTheme: RelationshipGraphShapeTheme) => void | Promise<void>;
  suppressLayoutNavigationBlocker?: boolean;
  fitViewDependency?: any;
}) {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('entity_form.organizations.membership_graph.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isRelationshipEditingEnabled
          ? 'Drag between member nodes to create a relationship. Click a link to edit, reverse, or delete it.'
          : 'This graph shows the character members and organization members selected in this form.'}
      </Typography>
      <RelationshipGraph
        entities={entities}
        relationships={relationships}
        codexCategories={{}}
        novelId={novelId}
        novelTheme={novelTheme}
        shapeThemeOverride={shapeThemeOverride}
        enableLayoutEditing={isRelationshipEditingEnabled}
        enableRelationshipEditing={isRelationshipEditingEnabled}
        showShapeThemeSelector={isRelationshipEditingEnabled}
        showEntityTooltip={false}
        isRelationshipSaving={isRelationshipSaving}
        onCreateRelationship={onCreateRelationship}
        onRelationshipClick={isRelationshipEditingEnabled ? onRelationshipClick : undefined}
        onLayoutPositionsChange={onLayoutPositionsChange}
        onSaveLayoutPositions={onSaveLayoutPositions}
        onShapeThemeChange={onShapeThemeChange}
        suppressLayoutNavigationBlocker={suppressLayoutNavigationBlocker}
        fitViewDependency={fitViewDependency}
        height={GRAPH_CANVAS_HEIGHT}
        minHeight={GRAPH_CANVAS_HEIGHT}
      />
    </Box>
  );
}

function MembershipSectionContent({
  organizationId,
  enableRelationshipEditing = false,
  disableAutoSave = false,
  isFormSubmitting = false,
  fitViewDependency,
}: MembershipSectionProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { novel, getNewId, updateEntityItem } = useNovelRepository();
  const { characters, isLoading: isLoadingCharacters } = useCharacters();
  const { data: organizationDocs, isLoading: isLoadingOrganizations } = useEntities<Organization>('organizations');
  const organizations = organizationDocs || [];
  const currentOrganization = organizationId
    ? organizations.find((organization) => organization.id === organizationId)
    : null;
  const { watch, setValue, register } = useFormContext<OrganizationFormValues>();
  const [isSavingGraphRelationship, setIsSavingGraphRelationship] = useState(false);
  const [isDeletingGraphRelationship, setIsDeletingGraphRelationship] = useState(false);
  const [selectedGraphRelationship, setSelectedGraphRelationship] = useState<Relationship | null>(null);
  const [relationshipPendingDelete, setRelationshipPendingDelete] = useState<Relationship | null>(null);

  useEffect(() => {
    register('organizationMemberGraphPositions');
    register('organizationMemberGraphTheme');
  }, [register]);

  const organizationTitles = (watch('organizationTitles') || []) as OrganizationTitle[];
  const organizationMembers = (watch('organizationMembers') || []) as OrganizationMember[];
  const parentOrganizationIds = (watch('parentOrganizationIds') || []) as string[];
  const childOrganizationIds = (watch('childOrganizationIds') || []) as string[];
  const childOrganizationTitles = (watch('childOrganizationTitles') || {}) as ChildOrganizationTitles;
  const organizationMemberGraphTheme = watch('organizationMemberGraphTheme') as RelationshipGraphShapeTheme | undefined;
  const organizationMemberGraphPositions = (watch('organizationMemberGraphPositions') || currentOrganization?.organizationMemberGraphPositions || {}) as Record<string, GraphPosition>;
  const relationships = (watch('organizationMemberGraphRelationships') || []) as Relationship[];

  const characterNameById = useMemo(() => (
    new Map(characters.map((character) => [character.id, character.name]))
  ), [characters]);

  const organizationNameById = useMemo(() => (
    new Map(organizations.map((organization) => [organization.id, organization.name]))
  ), [organizations]);

  const organizationOptions = useMemo(() => (
    organizations
      .filter((organization) => organization.id !== organizationId)
      .map((organization) => ({
        value: organization.id,
        label: organization.name,
      }))
  ), [organizationId, organizations]);

  const parentOrganizationOptions = useMemo(() => (
    organizationOptions.filter((organization) => !childOrganizationIds.includes(organization.value))
  ), [childOrganizationIds, organizationOptions]);

  const childOrganizationOptions = useMemo(() => (
    organizationOptions.filter((organization) => !parentOrganizationIds.includes(organization.value))
  ), [organizationOptions, parentOrganizationIds]);

  const memberOptions = useMemo<ComboboxOption[]>(() => {
    const characterMemberOptions = characters.map((character) => ({
      value: toCharacterMemberValue(character.id),
      label: t('entity_form.organizations.membership.member_option_character', {
        name: character.name,
      }),
      selectedLabel: character.name,
    }));
    const organizationMemberOptions = childOrganizationOptions.map((organization) => ({
      value: toOrganizationMemberValue(organization.value),
      label: t('entity_form.organizations.membership.member_option_organization', {
        name: organization.label,
      }),
      selectedLabel: organization.label,
    }));

    return [...characterMemberOptions, ...organizationMemberOptions];
  }, [characters, childOrganizationOptions, t]);

  const selectedMemberValues = useMemo(() => [
    ...organizationMembers.map((member) => toCharacterMemberValue(member.characterId)),
    ...childOrganizationIds.map(toOrganizationMemberValue),
  ], [childOrganizationIds, organizationMembers]);

  const titleSuggestions = useMemo(() => {
    const suggestions = new Set<string>();

    organizationTitles.forEach((title) => addTitleSuggestion(suggestions, title.name));
    Object.values(childOrganizationTitles).forEach((title) => addTitleSuggestion(suggestions, title));

    organizations.forEach((organization) => {
      const legacyTitleById = new Map((organization.organizationTitles || []).map((title) => [title.id, title.name]));

      organization.organizationTitles?.forEach((title) => addTitleSuggestion(suggestions, title.name));
      organization.organizationMembers?.forEach((member) => {
        addTitleSuggestion(suggestions, member.title);
        addTitleSuggestion(suggestions, member.titleId ? legacyTitleById.get(member.titleId) : '');
      });
      Object.values(organization.childOrganizationTitles || {}).forEach((title) => {
        addTitleSuggestion(suggestions, title);
      });
    });

    return [...suggestions].sort((a, b) => a.localeCompare(b));
  }, [childOrganizationTitles, organizationTitles, organizations]);

  const setMembers = (nextMembers: OrganizationMember[]) => {
    setValue('organizationMembers', nextMembers, { shouldDirty: true, shouldTouch: true });
  };

  const setChildOrganizationTitles = (nextTitles: ChildOrganizationTitles) => {
    setValue('childOrganizationTitles', nextTitles, { shouldDirty: true, shouldTouch: true });
  };

  const setParentOrganizations = (nextOrganizationIds: string[]) => {
    const nextChildOrganizationIds = childOrganizationIds.filter((id) => !nextOrganizationIds.includes(id));
    const nextChildOrganizationTitles = Object.fromEntries(
      Object.entries(childOrganizationTitles).filter(([id]) => nextChildOrganizationIds.includes(id)),
    );

    setValue('parentOrganizationIds', nextOrganizationIds, { shouldDirty: true, shouldTouch: true });
    setValue('childOrganizationIds', nextChildOrganizationIds, { shouldDirty: true, shouldTouch: true });
    setChildOrganizationTitles(nextChildOrganizationTitles);
  };

  const setChildOrganizations = (nextOrganizationIds: string[]) => {
    const nextChildOrganizationTitles = Object.fromEntries(
      Object.entries(childOrganizationTitles).filter(([id]) => nextOrganizationIds.includes(id)),
    );

    setValue('childOrganizationIds', nextOrganizationIds, { shouldDirty: true, shouldTouch: true });
    setValue(
      'parentOrganizationIds',
      parentOrganizationIds.filter((id) => !nextOrganizationIds.includes(id)),
      { shouldDirty: true, shouldTouch: true },
    );
    setChildOrganizationTitles(nextChildOrganizationTitles);
  };

  const persistGraphRelationships = useCallback(async (nextRelationships: Relationship[]) => {
    if (!organizationId) return;

    if (!disableAutoSave) {
      await updateEntityItem('organizations', organizationId, {
        organizationMemberGraphRelationships: nextRelationships,
      });
    }
    setValue('organizationMemberGraphRelationships', nextRelationships, { shouldDirty: true });
  }, [disableAutoSave, organizationId, setValue, updateEntityItem]);

  const handleCreateGraphRelationship = useCallback(async (
    source: RelationshipGraphConnectionEndpoint,
    target: RelationshipGraphConnectionEndpoint,
  ) => {
    setIsSavingGraphRelationship(true);
    try {
      const relationship: Relationship = {
        id: getNewId(),
        entityAId: source.entityId,
        entityAType: source.entityType,
        entityBId: target.entityId,
        entityBType: target.entityType,
        forwardDescription: 'is related to',
      };

      await persistGraphRelationships([...relationships, relationship]);
      toast({ title: t('relationships.relationship_added') });
    } catch (error) {
      console.error('Error creating relationship from organization membership graph:', error);
      toast({
        title: t('entity_form.common.update_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingGraphRelationship(false);
    }
  }, [getNewId, persistGraphRelationships, relationships, t, toast]);

  const handleUpdateGraphRelationship = useCallback(async (relationship: Relationship) => {
    if (relationship.managedBy) return;

    setIsSavingGraphRelationship(true);
    try {
      await persistGraphRelationships(relationships.map((item) => (
        item.id === relationship.id ? relationship : item
      )));
      toast({ title: t('relationships.relationship_updated') });
      setSelectedGraphRelationship(null);
    } catch (error) {
      console.error('Error updating relationship from organization membership graph:', error);
      toast({
        title: t('relationships.relationship_update_error_title'),
        description: error instanceof Error ? error.message : t('relationships.relationship_update_error_desc'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingGraphRelationship(false);
    }
  }, [persistGraphRelationships, relationships, t, toast]);

  const handleRequestDeleteGraphRelationship = useCallback((relationship: Relationship) => {
    if (relationship.managedBy) return;
    setSelectedGraphRelationship(null);
    setRelationshipPendingDelete(relationship);
  }, []);

  const handleDeleteGraphRelationship = useCallback(async () => {
    if (!relationshipPendingDelete || relationshipPendingDelete.managedBy) return;

    setIsDeletingGraphRelationship(true);
    try {
      await persistGraphRelationships(relationships.filter((relationship) => (
        relationship.id !== relationshipPendingDelete.id
      )));
      toast({ title: t('relationships.relationship_deleted') });
      setRelationshipPendingDelete(null);
    } catch (error) {
      console.error('Error deleting relationship from organization membership graph:', error);
      toast({
        title: t('relationships.relationship_delete_error_title'),
        description: error instanceof Error ? error.message : t('relationships.relationship_delete_error_desc'),
        variant: 'destructive',
      });
    } finally {
      setIsDeletingGraphRelationship(false);
    }
  }, [persistGraphRelationships, relationshipPendingDelete, relationships, t, toast]);

  const handleSaveGraphLayoutPositions = useCallback(async (positions: Record<string, GraphPosition>) => {
    if (!organizationId) return;

    if (!disableAutoSave) {
      await updateEntityItem('organizations', organizationId, {
        organizationMemberGraphPositions: positions,
      });
    }
    setValue('organizationMemberGraphPositions', positions, { shouldDirty: true });
  }, [disableAutoSave, organizationId, setValue, updateEntityItem]);

  const handleGraphLayoutPositionsChange = useCallback((positions: Record<string, GraphPosition>) => {
    setValue('organizationMemberGraphPositions', positions, { shouldDirty: true });
  }, [setValue]);

  const handleSaveGraphShapeTheme = useCallback(async (shapeTheme: RelationshipGraphShapeTheme) => {
    if (!organizationId) return;

    const previousShapeTheme = organizationMemberGraphTheme;
    setValue('organizationMemberGraphTheme', shapeTheme, { shouldDirty: true });

    try {
      if (!disableAutoSave) {
        await updateEntityItem('organizations', organizationId, {
          organizationMemberGraphTheme: shapeTheme,
        });
      }
    } catch (error) {
      setValue('organizationMemberGraphTheme', previousShapeTheme, { shouldDirty: true });
      console.error('Error saving organization graph theme:', error);
      toast({
        title: t('entity_form.common.update_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    }
  }, [disableAutoSave, organizationId, organizationMemberGraphTheme, setValue, t, toast, updateEntityItem]);

  const handleMemberSelectionChange = (selectedMemberIds: string[]) => {
    const selectedCharacterIds = selectedMemberIds
      .filter((value) => value.startsWith(CHARACTER_MEMBER_PREFIX))
      .map(fromCharacterMemberValue);
    const selectedOrganizationIds = selectedMemberIds
      .filter((value) => value.startsWith(ORGANIZATION_MEMBER_PREFIX))
      .map(fromOrganizationMemberValue);

    setMembers(selectedCharacterIds.map((characterId) => (
      organizationMembers.find((member) => member.characterId === characterId) || { characterId }
    )));
    setChildOrganizations(selectedOrganizationIds);
  };

  const getCharacterMemberTitle = (member: OrganizationMember) => (
    member.title || getLegacyTitleName(organizationTitles, member.titleId)
  );

  const handleAssignCharacterTitle = (characterId: string, value: string) => {
    const title = cleanTitle(value);

    setMembers(organizationMembers.map((member) => {
      if (member.characterId !== characterId) return member;

      return {
        characterId,
        ...(title ? { title } : {}),
      };
    }));
  };

  const handleAssignOrganizationTitle = (memberOrganizationId: string, value: string) => {
    const title = cleanTitle(value);
    const nextTitles = { ...childOrganizationTitles };

    if (title) {
      nextTitles[memberOrganizationId] = title;
    } else {
      delete nextTitles[memberOrganizationId];
    }

    setChildOrganizationTitles(nextTitles);
  };

  const memberGraphEntities = useMemo<RelationshipGraphEntity[]>(() => {
    const characterById = new Map(characters.map((character) => [character.id, character]));
    const organizationById = new Map(organizations.map((organization) => [organization.id, organization]));

    const seenCharacterIds = new Set<string>();
    const characterEntities: RelationshipGraphEntity[] = [];

    for (const member of organizationMembers) {
      if (!member.characterId || seenCharacterIds.has(member.characterId)) continue;
      seenCharacterIds.add(member.characterId);

      const character = characterById.get(member.characterId)
        || getFallbackEntitySummary(member.characterId, member.characterId, 'Character');

      characterEntities.push({
        ...character,
        entityType: 'characters',
        graphTypeLabel: getCharacterMemberTitle(member) || 'Character member',
        graphPosition: organizationMemberGraphPositions[`characters:${member.characterId}`],
      });
    }

    const seenOrgIds = new Set<string>();
    const organizationEntities: RelationshipGraphEntity[] = [];

    for (const memberOrganizationId of childOrganizationIds) {
      if (!memberOrganizationId || memberOrganizationId === organizationId || seenOrgIds.has(memberOrganizationId)) continue;
      seenOrgIds.add(memberOrganizationId);

      const organization = organizationById.get(memberOrganizationId);
      const fallback = getFallbackEntitySummary(memberOrganizationId, memberOrganizationId, 'Organization');

      organizationEntities.push({
        id: organization?.id || fallback.id,
        name: organization?.name || fallback.name,
        type: organization?.type || fallback.type,
        shortDescription: organization?.shortDescription || fallback.shortDescription,
        primaryImageId: organization?.primaryImageId ?? fallback.primaryImageId,
        mainImageUrl: organization?.mainImageUrl,
        mainImagePath: organization?.mainImagePath,
        graphPosition: organizationMemberGraphPositions[`organizations:${memberOrganizationId}`],
        updatedAt: organization?.audit?.updatedAt || organization?.audit?.createdAt || fallback.updatedAt,
        entityType: 'organizations',
        graphTypeLabel: childOrganizationTitles[memberOrganizationId] || organization?.type || 'Organization member',
      });
    }

    return [...characterEntities, ...organizationEntities];
  }, [
    characters,
    childOrganizationIds,
    childOrganizationTitles,
    organizationMemberGraphPositions,
    organizationId,
    organizationMembers,
    organizationTitles,
    organizations,
  ]);

  const memberTitleRows = [
    ...organizationMembers.map((member) => ({
      key: toCharacterMemberValue(member.characterId),
      name: characterNameById.get(member.characterId) || member.characterId,
      title: getCharacterMemberTitle(member),
      onTitleChange: (value: string) => handleAssignCharacterTitle(member.characterId, value),
    })),
    ...childOrganizationIds.map((memberOrganizationId) => ({
      key: toOrganizationMemberValue(memberOrganizationId),
      name: organizationNameById.get(memberOrganizationId) || memberOrganizationId,
      title: childOrganizationTitles[memberOrganizationId] || '',
      onTitleChange: (value: string) => handleAssignOrganizationTitle(memberOrganizationId, value),
    })),
  ];

  const memberGraphEntityNameByKey = new Map(memberGraphEntities.map((entity) => [
    `${entity.entityType}:${entity.id}`,
    entity.name,
  ]));

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('entity_form.organizations.membership.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('entity_form.organizations.membership.description')}
      </Typography>

      <Stack spacing={4}>
        <Box data-field="parentOrganizationIds">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
            {t('entity_form.organizations.membership.parent_organizations_heading')}
          </Typography>
          <MultiSelectComboBox
            options={parentOrganizationOptions}
            selected={parentOrganizationIds}
            onChange={setParentOrganizations}
            placeholder={t('entity_form.organizations.membership.parent_organizations_placeholder')}
            emptyPlaceholder={t('entity_form.organizations.membership.no_organizations')}
            isLoading={isLoadingOrganizations}
          />
        </Box>

        <Divider />

        <Box data-field="organizationMembers childOrganizationIds">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
            {t('entity_form.organizations.membership.members_heading')}
          </Typography>
          <Stack spacing={3}>
            <MultiSelectComboBox
              options={memberOptions}
              selected={selectedMemberValues}
              onChange={handleMemberSelectionChange}
              placeholder={t('entity_form.organizations.membership.members_placeholder')}
              emptyPlaceholder={t('entity_form.organizations.membership.no_members_found')}
              isLoading={isLoadingCharacters || isLoadingOrganizations}
              filterSelectedOptions
            />

            {memberTitleRows.length > 0 && (
              <Stack spacing={2}>
                {memberTitleRows.map((member) => (
                  <Grid container spacing={2} alignItems="center" key={member.key}>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {member.name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 7 }}>
                      <Autocomplete
                        freeSolo
                        options={titleSuggestions}
                        value={member.title}
                        onChange={(_, value) => member.onTitleChange(value || '')}
                        onInputChange={(_, value) => member.onTitleChange(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('entity_form.organizations.membership.member_title')}
                            placeholder={t('entity_form.organizations.membership.member_title_placeholder')}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>

        {organizationId && (
          <OrganizationRelationshipsGraph
            entities={memberGraphEntities}
            relationships={relationships}
            novelId={novel?.id}
            novelTheme={novel?.theme}
            shapeThemeOverride={organizationMemberGraphTheme}
            isRelationshipEditingEnabled={enableRelationshipEditing}
            isRelationshipSaving={isSavingGraphRelationship}
            onCreateRelationship={handleCreateGraphRelationship}
            onRelationshipClick={setSelectedGraphRelationship}
            onLayoutPositionsChange={handleGraphLayoutPositionsChange}
            onSaveLayoutPositions={handleSaveGraphLayoutPositions}
            onShapeThemeChange={handleSaveGraphShapeTheme}
            fitViewDependency={fitViewDependency}
            suppressLayoutNavigationBlocker={isFormSubmitting}
          />
        )}
      </Stack>

      <RelationshipEditorDialog
        open={!!selectedGraphRelationship}
        relationship={selectedGraphRelationship}
        sourceName={selectedGraphRelationship
          ? memberGraphEntityNameByKey.get(`${selectedGraphRelationship.entityAType}:${selectedGraphRelationship.entityAId}`)
          || selectedGraphRelationship.entityAId
          : ''}
        targetName={selectedGraphRelationship
          ? memberGraphEntityNameByKey.get(`${selectedGraphRelationship.entityBType}:${selectedGraphRelationship.entityBId}`)
          || selectedGraphRelationship.entityBId
          : ''}
        isManaged={!!selectedGraphRelationship?.managedBy}
        isSaving={isSavingGraphRelationship}
        showConnectorStyle
        onClose={() => setSelectedGraphRelationship(null)}
        onSave={handleUpdateGraphRelationship}
        onRequestDelete={handleRequestDeleteGraphRelationship}
      />

      <DeleteConfirmationModal
        isOpen={!!relationshipPendingDelete}
        onClose={() => setRelationshipPendingDelete(null)}
        onConfirm={() => void handleDeleteGraphRelationship()}
        isDeleting={isDeletingGraphRelationship}
        itemName={t('relationships.delete_item_name')}
        description={t('relationships.delete_description')}
      />
    </Paper>
  );
}

export function MembershipSection(props: MembershipSectionProps) {
  const { hasFeature } = useSubscription();

  if (!hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY)) {
    return null;
  }

  return <MembershipSectionContent {...props} />;
}
